import { BackButton } from '@/components/common/BackButton';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { PrintButton } from '@/components/common/PrintButton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { EmployeeReportHeader } from '../components/EmployeeReportHeader';
import { EmployeeReportPrint } from '../components/EmployeeReportPrint';
import { employeeReportColumns } from '../components/EmployeeReportColumns';
import { useEmployeeReport } from '../hooks/useEmployeeReport';
import { useEmployeeReportPage } from '../hooks/useEmployeeReportPage';
import type { EmployeeReportQueryOptionsFn } from '../api/attendance.api';

interface EmployeeReportPageProps {
  queryOptions: EmployeeReportQueryOptionsFn;
  userId: number;
  dateFrom?: string;
  dateTo?: string;
  title: string;
}

export function EmployeeReportPage({
  queryOptions,
  userId,
  dateFrom,
  dateTo,
  title,
}: EmployeeReportPageProps) {
  const {
    currentDateFrom,
    setCurrentDateFrom,
    currentDateTo,
    setCurrentDateTo,
    today,
    handleBack,
  } = useEmployeeReportPage({ dateFrom, dateTo });

  useDocumentTitle(title);

  const { employee, attendance, summaryItems, isLoading, error, refetch } = useEmployeeReport(
    queryOptions,
    {
      userId,
      ...(currentDateFrom !== '' && { dateFrom: currentDateFrom }),
      ...(currentDateTo !== '' && { dateTo: currentDateTo }),
    },
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        leading={<BackButton onClick={handleBack} />}
        actions={
          <div className="flex items-center gap-2">
            <DatePickerInput
              value={currentDateFrom}
              onChange={setCurrentDateFrom}
              maxDate={today}
            />
            <span className="text-sm text-muted-foreground">To</span>
            <DatePickerInput value={currentDateTo} onChange={setCurrentDateTo} maxDate={today} />
            <PrintButton disabled={isLoading || attendance.length === 0} />
          </div>
        }
      />
      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <EmployeeReportHeader employee={employee} />
          <StatCardGrid stats={summaryItems} isLoading={isLoading} />
          <DataTable
            data={attendance}
            columns={employeeReportColumns}
            isLoading={isLoading}
            stickyColumnId="date"
            className="lg:flex-1"
          />
          <EmployeeReportPrint
            title={title}
            employee={employee}
            attendance={attendance}
            dateFrom={currentDateFrom}
            dateTo={currentDateTo}
          />
        </>
      )}
    </div>
  );
}
